import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import { Document, Page } from "react-pdf";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import type { PdfDocumentMetrics, PdfOverlayRender, PdfSource } from "./types";
import { useObjectUrl, usePdfWorker } from "./hooks";

interface PdfDocumentViewerProps {
  source: PdfSource | string | null;
  page?: number;
  defaultPage?: number;
  onPageChange?: (page: number) => void;
  onDocumentLoad?: (metrics: { totalPages: number }) => void;
  width?: number;
  className?: string;
  showToolbar?: boolean;
  renderOverlay?: PdfOverlayRender;
  loadingFallback?: ReactNode;
  errorFallback?: ReactNode;
  toolbarActions?: ReactNode;
}

type DocumentFile = ComponentProps<typeof Document>["file"];

function toDocumentFile(source: PdfSource | string | null): DocumentFile | undefined {
  if (!source) {
    return undefined;
  }

  if (typeof source === "string") {
    return source;
  }

  if (source instanceof File) {
    return source;
  }

  if (source instanceof Uint8Array) {
    return { data: source };
  }

  return { data: new Uint8Array(source) };
}

export function PdfDocumentViewer({
  source,
  page,
  defaultPage = 1,
  onPageChange,
  onDocumentLoad,
  width = 600,
  className,
  showToolbar = true,
  renderOverlay,
  loadingFallback = <div className="p-4 text-center text-sm text-muted-foreground">Loading PDF…</div>,
  errorFallback = <div className="p-4 text-center text-sm text-destructive">Failed to load PDF.</div>,
  toolbarActions,
}: PdfDocumentViewerProps) {
  usePdfWorker();

  const [numPages, setNumPages] = useState(0);
  const [internalPage, setInternalPage] = useState(defaultPage);
  const [zoom, setZoom] = useState(1);
  const [viewport, setViewport] = useState<{ width: number; height: number; scale: number }>({
    width: width,
    height: width,
    scale: 1,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const documentSource = useMemo(() => toDocumentFile(source), [source]);
  const objectUrl = useObjectUrl(source);

  const activePage = page ?? internalPage;
  const hasMultiplePages = numPages > 1;

  useEffect(() => {
    if (page) {
      setInternalPage(page);
    }
  }, [page]);

  const handleDocumentLoadSuccess = useCallback(
    (pdf: { numPages: number }) => {
      setNumPages(pdf.numPages);
      onDocumentLoad?.({ totalPages: pdf.numPages });
      if (!page && internalPage > pdf.numPages) {
        setInternalPage(pdf.numPages);
      }
    },
    [internalPage, onDocumentLoad, page],
  );

  const handlePageLoadSuccess = useCallback(
    (loadedPage: any) => {
      const baseViewport = loadedPage.getViewport({ scale: 1 });
      const scale = width ? width / baseViewport.width : 1;
      setViewport({
        width: baseViewport.width,
        height: baseViewport.height,
        scale,
      });
    },
    [width],
  );

  const emitPageChange = useCallback(
    (nextPage: number) => {
      onPageChange?.(nextPage);
      if (page === undefined) {
        setInternalPage(nextPage);
      }
    },
    [onPageChange, page],
  );

  const goToPrevious = useCallback(() => {
    if (activePage <= 1) {
      return;
    }
    emitPageChange(activePage - 1);
  }, [activePage, emitPageChange]);

  const goToNext = useCallback(() => {
    if (activePage >= numPages) {
      return;
    }
    emitPageChange(activePage + 1);
  }, [activePage, emitPageChange, numPages]);

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const displayWidth = width * zoom;

  const metrics: PdfDocumentMetrics = useMemo(
    () => ({
      page: activePage,
      totalPages: numPages,
      viewport: {
        ...viewport,
        scale: viewport.scale * zoom,
      },
    }),
    [activePage, numPages, viewport, zoom],
  );

  const overlay = renderOverlay ? renderOverlay({ ...metrics, containerRef }) : null;

  return (
    <div className={className}>
      {showToolbar && (
        <div className="flex items-center justify-between gap-4 border-b bg-background px-4 py-2 text-sm">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevious}
              disabled={activePage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>
              Page {activePage} / {numPages || "—"}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNext}
              disabled={!hasMultiplePages || activePage >= numPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="ml-2 flex items-center gap-1 border-l pl-2">
              <Button
                variant="outline"
                size="sm"
                onClick={zoomOut}
                disabled={zoom <= 0.5}
                title="Zoom out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="min-w-[3rem] text-center text-xs text-muted-foreground">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={zoomIn}
                disabled={zoom >= 3}
                title="Zoom in"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
            {toolbarActions && (
              <div className="ml-2 border-l pl-2">
                {toolbarActions}
              </div>
            )}
          </div>
          {objectUrl && (
            <a
              href={objectUrl}
              rel="noreferrer"
              target="_blank"
              className="text-xs text-muted-foreground underline"
            >
              Open original
            </a>
          )}
        </div>
      )}

      <div className="flex w-full justify-center overflow-auto bg-muted/30 p-4">
        <div
          ref={containerRef}
          className="relative"
          style={{
            width: displayWidth,
            height: viewport.height * viewport.scale * zoom,
          }}
        >
          <Document
            file={documentSource}
            onLoadSuccess={handleDocumentLoadSuccess}
            loading={loadingFallback}
            error={errorFallback}
          >
            <Page
              pageNumber={activePage}
              width={displayWidth}
              onLoadSuccess={handlePageLoadSuccess}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </Document>
          {overlay}
        </div>
      </div>
    </div>
  );
}

